import React from 'react';

import { Alert, Empty, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { PendingTaskBreakdownType, PendingTaskCountType } from 'types/tasks';
import { formatTaskDateTime } from 'utils/tasks';

import { CenteredState, StatusBadge, TabContent, TaskName, TaskQueueTabHeader } from './TaskQueueTabContent';

const columns: ColumnsType<PendingTaskCountType> = [
  {
    title: 'Task type',
    dataIndex: 'name',
    key: 'name',
    width: '80%',
    render: (name) => <TaskName>{name}</TaskName>,
  },
  {
    title: 'Pending',
    dataIndex: 'count',
    key: 'count',
    align: 'right',
    width: '20%',
    render: (count) => count.toLocaleString(),
  },
];

type Props = {
  breakdown?: PendingTaskBreakdownType;
  isLoading: boolean;
  hasError: boolean;
};

const PendingTasksTab = ({ breakdown, isLoading, hasError }: Props) => (
  <TabContent>
    <TaskQueueTabHeader
      title="Pending by task type"
      description={`Queue snapshot loaded at ${formatTaskDateTime(
        breakdown?.fetched_at
      )}. Tasks are read in batches, with up to 10,000 analyzed for this breakdown; the overall pending total remains complete.`}
      action={<StatusBadge>{(breakdown?.total ?? 0).toLocaleString()} total</StatusBadge>}
    />
    {hasError ? (
      <Alert
        type="error"
        showIcon
        message="Pending task breakdown is unavailable"
        description="Refresh the browser page to try loading the queue breakdown again."
      />
    ) : isLoading ? (
      <CenteredState>
        <Spin />
      </CenteredState>
    ) : (
      <>
        {breakdown?.is_truncated && (
          <Alert
            type="warning"
            showIcon
            message={`Showing counts from the first ${breakdown.scanned_count.toLocaleString()} of ${breakdown.total.toLocaleString()} pending tasks`}
          />
        )}
        {!!breakdown?.unknown_count && (
          <Alert
            type="warning"
            showIcon
            message={`${breakdown.unknown_count.toLocaleString()} queued tasks could not be identified`}
          />
        )}
        <Table
          rowKey="name"
          columns={columns}
          dataSource={breakdown?.tasks ?? []}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: <Empty description="The pending queue is empty" /> }}
        />
      </>
    )}
  </TabContent>
);

export default PendingTasksTab;
